import {
  HISTORY_OPEN_EDIT,
  HISTORY_OPEN_FORK,
  HISTORY_OPEN_VIEW,
} from '@/constants/historyOpenModes';

/** Keys stripped from persisted params (plot data belongs in memory / API only). */
const HEAVY_PARAM_KEYS = ['plot', 'plot_cum', 'plotData', 'charts'];

/**
 * @param {unknown} params
 * @returns {unknown}
 */
export function stripHeavyParams(params) {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return params;
  const out = { ...params };
  for (const key of HEAVY_PARAM_KEYS) {
    delete out[key];
  }
  return out;
}

/**
 * @param {object|null|undefined} rec
 * @returns {object|null|undefined}
 */
export function slimRestoreRecord(rec) {
  if (!rec || typeof rec !== 'object') return rec;
  return {
    id: rec.id,
    plan_id: rec.plan_id,
    mode: rec.mode,
    structure_name: rec.structure_name,
    model_name: rec.model_name,
    model_type: rec.model_type,
    params: stripHeavyParams(rec.params),
    result: rec.result,
    created_at: rec.created_at,
  };
}

/**
 * Shrink payloads before sessionStorage write.
 * Edit/fork: keep run_id only — records are refetched on page load.
 * View: keep slim records (results needed for read-only UI).
 *
 * @param {object|null|undefined} payload
 * @returns {object|null|undefined}
 */
export function slimSessionPayload(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const openMode = payload.openMode;
  const out = { ...payload };

  if (openMode === HISTORY_OPEN_EDIT || openMode === HISTORY_OPEN_FORK) {
    if (out.run_id) {
      out.runRecords = [];
    } else if (Array.isArray(payload.runRecords)) {
      out.runRecords = payload.runRecords.map(slimRestoreRecord);
    }
    return out;
  }

  if (openMode === HISTORY_OPEN_VIEW && Array.isArray(payload.runRecords)) {
    out.runRecords = payload.runRecords.map(slimRestoreRecord);
    return out;
  }

  if (Array.isArray(payload.runRecords)) {
    out.runRecords = payload.runRecords.map(slimRestoreRecord);
  }
  return out;
}

/**
 * Run id used to refetch records when session stored metadata only.
 *
 * @param {object|null|undefined} payload
 * @returns {string|null}
 */
export function resolveRunIdForSessionRefetch(payload) {
  if (!payload) return null;
  if (payload.run_id) return payload.run_id;
  if (payload.openMode === HISTORY_OPEN_VIEW && payload.source_run_id) {
    return payload.source_run_id;
  }
  return null;
}
